import { drawing as d } from '@progress/kendo-drawing';
import { getTemplate } from '../services/template/get-template';
import { Cursors, INLINE, NEW_LINE_CHAR, WRAP } from './../constants';
import { randomId } from './../math';
import { Group, MultiLineTextBlock, TextBlock, textRect, getFont } from './../svg';
import { isString, isUndefined } from './../utils';
import { Observable } from './../utils/Observable';
import { deepExtend } from './../utils/deep-extend';
import { isFunction } from './../utils';
import { extend } from '../utils/scroller/utils';
/** @hidden */
export const getTextElementType = (options) => {
    let textWrap = options.textWrap;
    if (textWrap === undefined && (options.text.includes(NEW_LINE_CHAR) || !linesFit(options, textRect(options).size.width))) {
        textWrap = WRAP;
    }
    if (options.border || options.position === INLINE) {
        textWrap = WRAP;
    }
    return textWrap !== WRAP ? TextBlock : MultiLineTextBlock;
};
const linesFit = (options, maxWidth) => {
    const text = options.text || '';
    const styles = getFont(options);
    return !text.split(NEW_LINE_CHAR).find(line => d.util.measureText(line, styles).width > maxWidth);
};
const defaultOptions = {
    hover: {},
    cursor: Cursors.grip,
    content: {
        align: 'center middle'
    },
    selectable: true,
    serializable: true,
    enable: true
};
/** @hidden */
export class DiagramElement extends Observable {
    /**
     * Creates a new DiagramElement instance.
     * @param options Configuration options for the diagram element
     */
    constructor(options) {
        super();
        this.dataItem = (options || {}).dataItem;
        this.options = deepExtend({ id: randomId() }, defaultOptions, options);
        this.isSelected = false;
        this.visual = new Group({
            id: this.options.id,
            autoSize: this.options.autoSize
        });
        this.id = this.options.id;
        this._template();
    }
    /** @hidden */
    _getCursor(point) {
        if (this.adorner) {
            return this.adorner._getCursor(point);
        }
        return this.options.cursor;
    }
    /**
     * Gets or sets the visibility of the diagram element.
     * @param value If provided, sets the visibility. If undefined, returns the current visibility.
     * @returns The current visibility when used as a getter
     */
    visible(value) {
        if (isUndefined(value)) {
            return this.visual.visible();
        }
        else {
            this.visual.visible(value);
        }
    }
    /**
     * Gets or sets the bounds of the diagram element.
     * Base implementation returns null - should be overridden by subclasses.
     * @param value The bounds to set. If not provided, returns the current bounds.
     * @returns The bounds of the element or null
     */
    bounds(value) {
        if (isUndefined(value)) {
            return null;
        }
        return null;
    }
    /**
     * Refreshes the visual representation of the diagram element.
     * Triggers a redraw of the visual component.
     */
    refresh() {
        this.visual.redraw();
    }
    /**
     * Sets the position of the diagram element.
     * @param point The new position point with x and y coordinates
     */
    position(point) {
        this.options.x = point.x;
        this.options.y = point.y;
        this.visual.position(point);
    }
    /**
     * Returns a string representation of the diagram element.
     * @returns The ID of the element as a string
     */
    toString() {
        return this.options.id;
    }
    /**
     * Serializes the diagram element to a JSON object.
     * @returns An object containing the element's options and data item information
     */
    serialize() {
        // the options json object describes the shape perfectly. So this object can serve as shape serialization.
        const json = deepExtend({}, { options: this.options });
        if (this.dataItem) {
            json.dataItem = this.dataItem.toString();
        }
        return json;
    }
    /** @hidden */
    _content(content) {
        if (content !== undefined) {
            const options = this.options;
            if (isString(content)) {
                options.content.text = content;
            }
            else {
                deepExtend(options.content, content);
            }
            const contentOptions = options.content;
            let contentVisual = this._contentVisual;
            if (contentVisual && getTextElementType(contentOptions).name !== contentVisual.name) {
                const drawingElement = contentVisual.drawingContainer();
                drawingElement.parent.remove(drawingElement);
                this._contentVisual = contentVisual = null;
            }
            if (!contentVisual) {
                this._createContentVisual(contentOptions);
            }
            else {
                this._updateContentVisual(contentOptions);
            }
        }
        return this.options.content.text;
    }
    /** @hidden */
    _createContentVisual(options) {
        if (options.text) {
            options.shapeSize = this.bounds();
            const Type = getTextElementType(options);
            this._contentVisual = new Type(options);
            this._contentVisual._includeInBBox = false;
            this.visual.append(this._contentVisual);
        }
    }
    /** @hidden */
    _updateContentVisual(options) {
        this._contentVisual.redraw(options);
    }
    /** @hidden */
    _hitTest(point) {
        const bounds = this.bounds();
        return this.visible() && bounds.contains(point) && this.options.enable;
    }
    /** @hidden */
    _template() {
        if (this.options.content.template) {
            const data = this.dataItem || {};
            const templateOptions = extend({}, this.options.content, {
                dataItem: this.dataItem || this.options.dataItem,
            });
            const elementTemplate = getTemplate(templateOptions);
            if (isFunction(elementTemplate)) {
                this.options.content.text = elementTemplate(data);
            }
            else if (isString(elementTemplate)) {
                this.options.content.text = elementTemplate;
            }
        }
    }
    /** @hidden */
    _canSelect() {
        return this.options.selectable !== false;
    }
    /**
     * Converts the diagram element to a JSON representation.
     * @returns Object containing the element's ID
     */
    toJSON() {
        return {
            id: this.options.id
        };
    }
}
